import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

type WishlistContextValue = {
    ids: Set<string>;
    count: number;
    has: (productId: string) => boolean;
    toggle: (productId: string) => boolean;
    clear: () => void;
};

const WishlistContext = createContext<WishlistContextValue | undefined>(undefined);
const STORAGE_KEY = "tsgo-wishlist";

function readInitial(): string[] {
    if (typeof window === "undefined") return [];
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as string[];
        return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
    } catch {
        return [];
    }
}

/**
 * Client-only wishlist — same architectural pattern as the cart store.
 * A backend endpoint will replace this in a later step; the shape stays the same.
 */
export function WishlistProvider({ children }: { children: React.ReactNode }) {
    const [ids, setIds] = useState<Set<string>>(() => new Set(readInitial()));

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
        } catch {
            /* ignore */
        }
    }, [ids]);

    const has = useCallback((id: string) => ids.has(id), [ids]);

    const toggle = useCallback((id: string) => {
        let added = false;
        setIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                added = false;
            } else {
                next.add(id);
                added = true;
            }
            return next;
        });
        return added;
    }, []);

    const clear = useCallback(() => setIds(new Set()), []);

    const value = useMemo<WishlistContextValue>(
        () => ({ ids, count: ids.size, has, toggle, clear }),
        [ids, has, toggle, clear],
    );

    return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

export function useWishlist() {
    const ctx = useContext(WishlistContext);
    if (!ctx) throw new Error("useWishlist must be used within <WishlistProvider>");
    return ctx;
}
